import focus from '../primitives/focus.module.css';
import styles from './NavBar.module.css';

export interface NavItem {
  readonly id: string;
  readonly label: string;
  /** A single character or emoji standing in for the screen book's line icons. */
  readonly glyph: string;
}

export interface NavBarProps {
  readonly items: readonly NavItem[];
  readonly currentId: string;
  readonly onNavigate: (id: string) => void;
  /** Names the navigation for a screen reader, and distinguishes it from any other nav on the page. */
  readonly label?: string;
}

/**
 * The four tabs at the bottom of the user app: Home, Ledger, Reports, Settings (`LED-01`).
 *
 * Each tab is a button and the current one carries `aria-current="page"`, so "where am I" is answered for
 * a screen reader and not only by the weight of the label. Every tab is a tab stop, which is what a
 * keyboard user expects of a navigation landmark - unlike a chip group, where the whole set is one stop.
 *
 * The label under each glyph is not decoration: an icon-only bar makes people guess, and guessing about
 * where their money is recorded is not a thing to ask of anyone.
 */
export function NavBar({ items, currentId, onNavigate, label = 'Main' }: NavBarProps): React.JSX.Element {
  return (
    <nav className={styles.nav} aria-label={label}>
      {items.map((item) => {
        const current = item.id === currentId;

        return (
          <button
            key={item.id}
            type="button"
            className={[styles.tab, focus.focusable, current ? styles.current : ''].filter(Boolean).join(' ')}
            aria-current={current ? 'page' : undefined}
            onClick={() => {
              onNavigate(item.id);
            }}
          >
            <span className={styles.glyph} aria-hidden="true">
              {item.glyph}
            </span>
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}

/** The four tabs the screen book shows, in the order it shows them. */
export const defaultNavItems: readonly NavItem[] = [
  { id: 'home', label: 'Home', glyph: '⌂' },
  { id: 'ledger', label: 'Ledger', glyph: '▤' },
  { id: 'reports', label: 'Reports', glyph: '▥' },
  { id: 'settings', label: 'Settings', glyph: '⚙' },
];
